/**
 * The first minutes of a live session, made legible.
 *
 * Starting a session kicks off three different kinds of work. They used to be
 * represented by one spinner whose label changed only after something had
 * finished, so a GM could not tell a healthy cold start from a dead worker.
 * This names the whole sequence up front and gives them their own campaign
 * notes to read while it runs.
 */
import { useEffect, useMemo, useState } from 'react';

import {
  getSessionSetup,
  type SessionSetup,
} from '@/api/client';
import type { Campaign, Checkpoint } from '@/api/types';
import { parsePremise } from '@/components/CampaignReference';

type Stage = 0 | 1 | 2;

interface Props {
  sessionId: string;
  campaign: Campaign | null;
  narration: string;
  checkpoints: Checkpoint[];
  /** Keeps the dev preview honest without calling an authenticated endpoint. */
  setupPreview?: SessionSetup;
}

const STEPS = [
  'Writing your opening',
  'Preparing likely choices',
  'Drawing scenes',
] as const;

export function SessionStartup({
  sessionId,
  campaign,
  narration,
  checkpoints,
  setupPreview,
}: Props) {
  const complete = initialCheckpointComplete(checkpoints);
  const [setup, setSetup] = useState<SessionSetup | null>(setupPreview ?? null);

  useEffect(() => {
    setSetup(setupPreview ?? null);
    if (setupPreview || complete) return;
    let cancelled = false;
    void getSessionSetup(sessionId)
      .then((next) => {
        if (!cancelled) setSetup(next);
      })
      .catch(() => {
        // Campaign data below is already enough for a useful fallback. This
        // reference is helpful during startup, never a reason to block it.
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, setupPreview, complete]);

  const sections = useMemo(
    () => parsePremise(setup?.premise ?? campaign?.premise ?? ''),
    [setup?.premise, campaign?.premise],
  );

  if (complete) return null;

  const stage: Stage = !narration ? 0 : checkpoints.length === 0 ? 1 : 2;
  const begins = sections.find((s) => s.label === 'Where the session begins')?.body;
  const world = sections.find((s) => s.label === 'World, setting, and premise')?.body;
  const intent = setup?.session_intent;

  return (
    <section className="startup" aria-label="Preparing your session">
      <header className="startup__head">
        <span className="spinner" aria-hidden="true" />
        <div>
          <p className="startup__eyebrow">Preparing your session</p>
          <h2 className="startup__title">{activeMessage(stage)}</h2>
        </div>
      </header>

      <ol className="startup__steps" aria-label="Session preparation progress">
        {STEPS.map((label, index) => {
          const state = index < stage ? 'done' : index === stage ? 'active' : 'next';
          return (
            <li className={`startup__step startup__step--${state}`} key={label}>
              <span className="startup__step-mark" aria-hidden="true">
                {state === 'done' ? '✓' : index + 1}
              </span>
              <span>{label}</span>
            </li>
          );
        })}
      </ol>

      <div className="startup__reminders">
        <section className="startup__reminder startup__reminder--wide">
          <h3>Where tonight begins</h3>
          <p>{begins || world || 'Your campaign setup is being loaded.'}</p>
        </section>

        <section className="startup__reminder">
          <h3>Party</h3>
          {setup ? <PartyReminder setup={setup} /> : <p>Loading the table…</p>}
        </section>

        <section className="startup__reminder">
          <h3>What you wanted tonight</h3>
          <p>{intent || 'No special direction. Follow the table.'}</p>
        </section>
      </div>
    </section>
  );
}

function PartyReminder({ setup }: { setup: SessionSetup }) {
  if (setup.party.length === 0) {
    return <p>No party saved. Combat will use a stand-in party.</p>;
  }
  return (
    <ul className="startup__party">
      {setup.party.map((member, index) => (
        <li key={`${member.name}-${index}`}>
          <strong>{member.name}</strong>
          <span>{member.level ? `Level ${member.level}` : 'Level not set'}</span>
        </li>
      ))}
    </ul>
  );
}

export function initialCheckpointComplete(checkpoints: Checkpoint[]): boolean {
  if (checkpoints.length === 0) return false;
  // Checkpoints are newest-first. Startup belongs to the oldest one; later
  // checkpoints rendering must not bring the opening sequence back.
  const first = checkpoints[checkpoints.length - 1]!;
  if (first.status === 'ready' || first.status === 'failed') return true;
  const frames = first.frames ?? [];
  return (
    frames.length > 0 &&
    frames.every((frame) => frame.status === 'ready' || frame.status === 'failed')
  );
}

function activeMessage(stage: Stage): string {
  if (stage === 0) return 'Writing the words that bring everyone into the scene';
  if (stage === 1) return 'Preparing what your players are most likely to find next';
  return 'Turning those possibilities into scenes you can glance at';
}
